import { API_URL } from "..";
import { baseRequest } from "../request";
import { REFRESH_REQUEST_TIMEOUT_MS } from "./refresh_token_action";
import { recordLogoutReply, setLogoutPending } from "./logout_pending";
import { clearLastActivity } from "./idle_sign_out";
import { endSessionScope, releaseSharedSessionNonce } from "./session_scope";
import { clearSharedSessionDeadlines } from "./session_deadlines";

// The one logout primitive: every caller keeps the signature it has today, `await` it and move on.
//
// the request carries the same 10s timeout as the refresh, so a hung connection ends
// the sign-out instead of stalling it, and any reply that is not the server's definitive answer
// leaves a pending-logout marker behind for the next boot to retry.
//
// `sessionEnded`: the server has already ended the session (a request answered 401), so there is no
// last attempt to save drafts with a token that no longer works.
export const clearIdentitySession = async ({
  sessionEnded = false,
}: { sessionEnded?: boolean } = {}): Promise<void> => {
  // private work captured under this session stops before the logout goes out, after one last attempt
  // to save what it holds; the other tabs of the sign-in hear it end only once the server has answered
  // (below)
  const ended = endSessionScope({ signingOut: !sessionEnded });
  try {
    const result = await baseRequest({
      method: "post",
      url: `${API_URL}/v1/web/identity-logout`,
      timeout: REFRESH_REQUEST_TIMEOUT_MS,
    });
    recordLogoutReply(result?.statusCode);
  } catch {
    // `baseRequest` reports transport failures as `statusCode: null` rather than throwing, but a
    // throw from anywhere else is still a logout the server never confirmed
    setLogoutPending();
  } finally {
    window.sessionStorage.removeItem("labfront");
    // left behind, it would read as a session left running once the idle limit passed, and the
    // next boot would sign out again and blame inactivity
    clearLastActivity();
    if (ended) {
      releaseSharedSessionNonce(ended.nonce);
      clearSharedSessionDeadlines(ended.nonce);
    }
  }
};
